'use client';

import { useEffect, useState } from 'react';
import { Blockquote } from '@tiptap/extension-blockquote';
import { Bold } from '@tiptap/extension-bold';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Document } from '@tiptap/extension-document';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Heading } from '@tiptap/extension-heading';
import { History } from '@tiptap/extension-history';
import { Italic } from '@tiptap/extension-italic';
import { ListItem } from '@tiptap/extension-list-item';
import { Mention } from '@tiptap/extension-mention';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import { marked } from 'marked';
import { Button, Modal, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { BubbleCustomMenu } from './BubbleCustomMenu';
import { CustomCode } from './CustomCode';
import CustomImage from './CustomImage';
import { CustomMenu } from './CustomMenu';
import { Dropzone } from './Dropzone';

export const Editor = ({
  setEditorContent,
  content,
}: {
  setEditorContent?: (content: string) => void;
  content?: any;
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [markdownOpened, { open: openMarkdown, close: closeMarkdown }] = useDisclosure(false);
  const [editorImageUrl, setEditorImageUrl] = useState<string | null>(null);
  const [markdownText, setMarkdownText] = useState('');

  const editor = useEditor({
    extensions: [
      // Use individual extensions instead of StarterKit
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      History,
      Heading,
      // Other extensions
      CustomCode, // Our custom code extension
      CustomImage.configure({ inline: true, allowBase64: true }),
      Blockquote,
      Underline,
      HardBreak,
      BulletList,
      OrderedList,
      ListItem,
      Mention,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Gapcursor,
    ],
    content: content ?? '',
    onUpdate: ({ editor }) => {
      try {
        setEditorContent && setEditorContent(editor.getHTML());
      } catch (error) {
        console.error('Error updating editor content:', error);
      }
    },
  });

  useEffect(() => {
    try {
      if (editorImageUrl && editor) {
        editor.chain().focus().setImage({ src: editorImageUrl }).run();
      }
    } catch (error) {
      console.error('Error setting image:', error);
    }
  }, [editorImageUrl, editor]);

  const applyMarkdown = async () => {
    if (!editor || !markdownText.trim()) {
      return;
    }
    try {
      const html = (await marked.parse(markdownText, { async: true })) as string;
      editor.commands.setContent(html, false);
      setEditorContent && setEditorContent(editor.getHTML());
      setMarkdownText('');
      closeMarkdown();
    } catch (error) {
      console.error('Error converting markdown:', error);
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <CustomMenu editor={editor} open={open} openMarkdown={openMarkdown} />
      <BubbleCustomMenu editor={editor} />
      <EditorContent editor={editor} />
      <Modal opened={opened} onClose={close} title="Upload image">
        <Dropzone setImageUrl={setEditorImageUrl} imageUrl={editorImageUrl} />
      </Modal>
      <Modal opened={markdownOpened} onClose={closeMarkdown} title="Coller du Markdown" size="xl">
        <Textarea
          label="Markdown"
          placeholder="Collez votre article au format Markdown ici…"
          value={markdownText}
          onChange={(e) => setMarkdownText(e.currentTarget.value)}
          minRows={10}
          mb="md"
        />
        <Button onClick={applyMarkdown} disabled={!markdownText.trim()} fullWidth>
          Convertir en HTML
        </Button>
      </Modal>
    </div>
  );
};
